/**
 * Invoice Details Page
 * ====================
 * Lists all invoices with extracted data from OCR/GPT processing.
 * Uses DataDashboard for a full-featured table view.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DataDashboard, { Column } from '../../components/common/DataDashboard';
import documentApi, { DocumentItem, EXPENSE_CATEGORIES } from '../../services/documentApi';
import InvoiceExportModal, { DateRange } from './InvoiceExportModal';
import { exportInvoicesToExcel } from '../../utils/invoiceExport';
import './InvoiceDetails.scss';

const InvoiceDetails: React.FC = () => {
    const [data, setData] = useState<DocumentItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 1,
        has_next: false,
        has_prev: false
    });

    // Export modal state
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    // Flatten nested data for sorting/filtering to work
    const flattenedData = useMemo(() => {
        return data.map(item => ({
            ...item,
            vendor_name: item.extracted_data?.vendor?.name || item.extracted_data?.supplier_name || '',
            supplier_tax_id: item.extracted_data?.supplier_tax_id || item.extracted_data?.vendor?.tax_id || '',
            supplier_address: item.extracted_data?.supplier_address || item.extracted_data?.vendor?.address || '',
            invoice_number: item.extracted_data?.invoice_number || '',
            invoice_date: item.extracted_data?.invoice_date || '',
            due_date: item.extracted_data?.due_date || '',
            total_amount: item.extracted_data?.financials?.total_amount || item.extracted_data?.total_amount || 0,
            currency: item.extracted_data?.financials?.currency || item.extracted_data?.currency || '',
            total_tax_amount: item.extracted_data?.total_tax_amount || item.extracted_data?.financials?.tax || 0,
            net_amount: item.extracted_data?.net_amount || item.extracted_data?.financials?.subtotal || 0,
            line_items_count: item.extracted_data?.line_items?.length || item.extracted_data?.items?.length || 0,
            expense_category: item.expense_category || item.extracted_data?.expense_category || ''
        }));
    }, [data]);

    const fetchData = useCallback(async (params: any = {}) => {
        setLoading(true);
        setError(null);
        try {
            const page = params.page || 1;
            const limit = params.limit || 20;

            const response = await documentApi.getDocuments(page, limit, 'invoice');

            if (response.success && response.data) {
                setData(response.data.documents);
                setPagination({
                    page: response.data.page,
                    limit: response.data.page_size,
                    total: response.data.total,
                    pages: Math.ceil(response.data.total / response.data.page_size),
                    has_next: response.data.has_next,
                    has_prev: response.data.has_prev
                });
            } else {
                setError(response.message || 'Failed to fetch invoices');
            }
        } catch (err) {
            setError('Error connecting to server');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDataFetch = (newParams: any) => {
        fetchData(newParams);
    };

    const CURRENCY_MAP: Record<string, string> = { 'TL': 'TRY', '₺': 'TRY', '$': 'USD', '€': 'EUR', '£': 'GBP' };
    const formatCurrency = (amount?: number, currency?: string): string => {
        if (amount === undefined || amount === null) return '-';
        const c = currency || 'TRY';
        const code = CURRENCY_MAP[c] || CURRENCY_MAP[c.toUpperCase()] || c.toUpperCase();
        try {
            return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: code }).format(amount);
        } catch {
            return `${amount.toLocaleString('tr-TR')} ${code}`;
        }
    };

    const handleSaveChanges = useCallback(async (changes: Record<string, Record<string, any>>) => {
        try {
            const promises = Object.entries(changes).map(([docId, fields]) =>
                documentApi.updateDocumentFields(docId, fields)
            );
            await Promise.all(promises);
            setIsEditing(false);
            fetchData({ page: pagination.page, limit: pagination.limit });
        } catch (err) {
            console.error('Failed to save changes:', err);
        }
    }, [fetchData, pagination]);

    const columns: Column[] = useMemo(() => [
        {
            key: 'filename',
            title: 'Dosya Adı',
            type: 'text',
            sortable: true,
            width: '200px',
            render: (value: string) => (
                <span title={value} className="filename-cell">
                    {value}
                </span>
            )
        },
        {
            key: 'vendor_name',
            title: 'Tedarikçi',
            type: 'text',
            sortable: true,
            editable: true,
            width: '180px',
            render: (value: string) => <span title={value || '-'}>{value || '-'}</span>
        },
        {
            key: 'expense_category',
            title: 'Gider Türü',
            type: 'dropdown',
            sortable: true,
            filterable: true,
            editable: true,
            width: '140px',
            options: Object.entries(EXPENSE_CATEGORIES).map(([key, val]) => ({
                value: key,
                label: val.tr
            })),
            render: (value: string) => {
                const cat = EXPENSE_CATEGORIES[value];
                return <span className={`category-badge category-${value || 'other'}`}>{cat?.tr || '-'}</span>;
            }
        },
        {
            key: 'supplier_tax_id',
            title: 'Vergi No',
            type: 'text',
            sortable: true,
            editable: true,
            width: '120px',
            defaultHidden: true,
            render: (value: string) => value || '-'
        },
        {
            key: 'supplier_address',
            title: 'Adres',
            type: 'text',
            sortable: true,
            editable: true,
            width: '200px',
            defaultHidden: true,
            render: (value: string) => <span title={value || '-'}>{value || '-'}</span>
        },
        {
            key: 'invoice_number',
            title: 'Fatura No',
            type: 'text',
            sortable: true,
            editable: true,
            width: '120px',
            render: (value: string) => value || '-'
        },
        {
            key: 'invoice_date',
            title: 'Fatura Tarihi',
            type: 'text',
            sortable: true,
            editable: true,
            width: '110px',
            render: (value: string) => {
                if (!value) return '-';
                try { return new Date(value).toLocaleDateString('tr-TR'); }
                catch { return value; }
            }
        },
        {
            key: 'due_date',
            title: 'Vade Tarihi',
            type: 'text',
            sortable: true,
            editable: true,
            width: '110px',
            defaultHidden: true,
            render: (value: string) => {
                if (!value) return '-';
                try { return new Date(value).toLocaleDateString('tr-TR'); }
                catch { return value; }
            }
        },
        {
            key: 'total_amount',
            title: 'Toplam Tutar',
            type: 'number',
            sortable: true,
            editable: true,
            width: '130px',
            render: (value: number, row: any) => {
                const currency = row.extracted_data?.financials?.currency || row.extracted_data?.currency || 'TRY';
                return formatCurrency(value, currency);
            }
        },
        {
            key: 'currency',
            title: 'Para Birimi',
            type: 'text',
            sortable: true,
            editable: true,
            width: '80px',
            defaultHidden: true,
            render: (value: string) => value || '-'
        },
        {
            key: 'total_tax_amount',
            title: 'KDV',
            type: 'number',
            sortable: true,
            editable: true,
            width: '110px',
            defaultHidden: true,
            render: (value: number, row: any) => {
                const currency = row.extracted_data?.financials?.currency || row.extracted_data?.currency || 'TRY';
                return formatCurrency(value, currency);
            }
        },
        {
            key: 'net_amount',
            title: 'Net Tutar',
            type: 'number',
            sortable: true,
            editable: true,
            width: '120px',
            defaultHidden: true,
            render: (value: number, row: any) => {
                const currency = row.extracted_data?.financials?.currency || row.extracted_data?.currency || 'TRY';
                return formatCurrency(value, currency);
            }
        },
        {
            key: 'line_items_count',
            title: 'Kalem Sayısı',
            type: 'number',
            sortable: true,
            width: '100px',
            render: (value: number) => value > 0 ? value : '-'
        },
        {
            key: 'extraction_status',
            title: 'Extraction',
            type: 'status',
            sortable: true,
            width: '110px',
            render: (value: string) => {
                const statusMap: Record<string, { label: string; class: string }> = {
                    'completed': { label: 'Tamamlandı', class: 'success' },
                    'processing': { label: 'İşleniyor', class: 'warning' },
                    'pending': { label: 'Bekliyor', class: 'info' },
                    'failed': { label: 'Başarısız', class: 'error' }
                };
                const status = statusMap[value] || { label: value || 'Bilinmiyor', class: 'default' };
                return (
                    <span className={`status-badge status-${status.class}`}>
                        {status.label}
                    </span>
                );
            }
        },
        {
            key: 'created_at',
            title: 'Yüklenme',
            type: 'date',
            sortable: true,
            width: '100px',
            render: (value: string) => {
                if (!value) return '-';
                try {
                    return new Date(value).toLocaleDateString('tr-TR');
                } catch {
                    return '-';
                }
            }
        }
    ], []);

    const handleRowClick = (item: DocumentItem) => {
        // Navigate to reconciliation page if comparison exists
        if (item.comparison?.linked_quote_id) {
            window.location.href = `/reconciliation/${item.id}`;
        }
    };

    // Fetch all invoices for export (paginate through all pages)
    const fetchAllInvoicesForExport = async (): Promise<DocumentItem[]> => {
        const allInvoices: DocumentItem[] = [];
        let currentPage = 1;
        let hasMore = true;
        const pageSize = 100; // Max page size

        while (hasMore) {
            const response = await documentApi.getDocuments(currentPage, pageSize, 'invoice');
            if (response.success && response.data) {
                allInvoices.push(...response.data.documents);
                hasMore = response.data.has_next;
                currentPage++;
            } else {
                hasMore = false;
            }
        }

        return allInvoices;
    };

    const handleExportClick = () => {
        setIsExportModalOpen(true);
    };

    const handleExportConfirm = async (dateRange: DateRange) => {
        setIsExporting(true);
        try {
            // Fetch all invoices
            const allInvoices = await fetchAllInvoicesForExport();

            // Export to Excel with date range filtering
            exportInvoicesToExcel(allInvoices, {
                startDate: dateRange.startDate,
                endDate: dateRange.endDate
            });

            setIsExportModalOpen(false);
        } catch (err) {
            console.error('Export failed:', err);
            alert('Export sirasinda bir hata olustu.');
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <>
            <DataDashboard
                title="Invoice Details"
                collection="invoice-details"
                data={flattenedData}
                columns={columns}
                pageDescription="OCR ile cikarilan fatura detaylarini goruntuleyiniz"
                onDataChange={setData}
                onRowClick={handleRowClick}
                onExport={handleExportClick}
                showAddButton={false}
                showEditButton={true}
                showDeleteButton={false}
                showExportButton={true}
                isEditing={isEditing}
                onEditToggle={() => setIsEditing(prev => !prev)}
                onSaveChanges={handleSaveChanges}
                isLoading={loading}
                error={error}
                paginationInfo={pagination}
                onDataFetch={handleDataFetch}
            />
            <InvoiceExportModal
                isOpen={isExportModalOpen}
                onClose={() => setIsExportModalOpen(false)}
                onExport={handleExportConfirm}
                isLoading={isExporting}
            />
        </>
    );
};

export default InvoiceDetails;
